import httpClient from "@utils/httpClient";

/**
 * Organizational Admin Interfaces
 */
export interface OrgAdmin {
  id: number;
  user_id: string;
  organization_name: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  domains?: OrganizationDomain[];
}

export interface OrganizationDomain {
  domain: string;
}

export interface User {
  userId: string;
  name: string;
  email: string;
  rollId: number;
}

export interface Domain {
  domain: string;
}

export interface CreateOrgAdminRequest {
  userId: string;
  organizationName: string;
  domains: string[];
}

export interface CreateUserAndOrgAdminRequest {
  name: string;
  email: string;
  organizationName: string;
  domains: string[];
}

export interface UpdateOrgAdminRequest {
  organizationName?: string;
  status?: 'active' | 'inactive';
  domains?: string[];
}

export interface OrgAdminDetails {
  id: number;
  organization_name: string;
  status: 'active' | 'inactive';
  user_name: string;
  user_email: string;
  created_at: string;
  updated_at: string;
  domains: Array<{ domain: string }>;
  assessments: Array<{
    id: number;
    title: string;
    created_at: string;
  }>;
  users: Array<{
    userId: string;
    name: string;
    email: string;
    rollId: number;
    createdAt: string;
  }>;
  stats: {
    total_domains: number;
    total_assessments: number;
    total_users: number;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  status?: string;
  data?: T;
  message?: string;
  error?: string | { message: string };
  timestamp?: string;
}

/**
 * Get all organizational admins
 */
export const getAllOrgAdmins = async (): Promise<ApiResponse<OrgAdmin[]>> => {
  try {
    const response = await httpClient.get('/org-admin');
    // Backend returns { admins: [...], total: number }
    const admins = response.data?.admins || response.admins || [];
    return {
      success: true,
      data: admins,
      message: response.message || 'Organizational admins fetched successfully'
    };
  } catch (error: any) {
    console.error('getAllOrgAdmins error:', error);
    return {
      success: false,
      data: [],
      error: error.message || 'Failed to fetch organizational admins'
    };
  }
};

/**
 * Get all users (for dropdown in create admin form)
 */
export const getAllUsers = async (): Promise<ApiResponse<User[]>> => {
  try {
    const response = await httpClient.get('/org-admin/users/all');
    // Backend returns { users: [...], total: number }
    const users = response.data?.users || response.users || [];
    return {
      success: true,
      data: users,
      message: response.message || 'Users fetched successfully'
    };
  } catch (error: any) {
    console.error('getAllUsers error:', error);
    return {
      success: false,
      data: [],
      error: error.message || 'Failed to fetch users'
    };
  }
};

/**
 * Get all unique domains (for dropdown in create admin form)
 */
export const getAllDomains = async (): Promise<ApiResponse<Domain[]>> => {
  try {
    const response = await httpClient.get('/org-admin/domains/all');
    // Backend returns { domains: [...], total: number }
    const domains = response.data?.domains || response.domains || [];
    return {
      success: true,
      data: domains,
      message: response.message || 'Domains fetched successfully'
    };
  } catch (error: any) {
    console.error('getAllDomains error:', error);
    return {
      success: false,
      data: [],
      error: error.message || 'Failed to fetch domains'
    };
  }
};

/**
 * Create a new organizational admin
 */
export const createOrgAdmin = async (
  data: CreateOrgAdminRequest
): Promise<ApiResponse<OrgAdmin>> => {
  try {
    const response = await httpClient.post('/org-admin', data);
    // Backend returns { message: "...", admin: {...} }
    const admin = response.data?.admin || response.admin;
    return {
      success: true,
      data: admin,
      message: response.data?.message || response.message || 'Organizational admin created successfully'
    };
  } catch (error: any) {
    console.error('createOrgAdmin error:', error);
    return {
      success: false,
      error: error.message || 'Failed to create organizational admin'
    };
  }
};

/**
 * Create new user and organizational admin
 */
export const createUserAndOrgAdmin = async (
  data: CreateUserAndOrgAdminRequest
): Promise<ApiResponse<{ user: User; admin: OrgAdmin }>> => {
  try {
    const response = await httpClient.post('/org-admin/create-with-user', data);
    // Backend returns { message: "...", user: {...}, admin: {...}, temporaryPassword: "..." }
    const result = {
      user: response.data?.user || response.user,
      admin: response.data?.admin || response.admin
    };
    return {
      success: true,
      data: result,
      message: response.data?.message || response.message || 'User and organizational admin created successfully'
    };
  } catch (error: any) {
    console.error('createUserAndOrgAdmin error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to create user and organizational admin'
    };
  }
};

/**
 * Update an organizational admin
 */
export const updateOrgAdmin = async (
  id: number,
  data: UpdateOrgAdminRequest
): Promise<ApiResponse<OrgAdmin>> => {
  try {
    const response = await httpClient.put(`/org-admin/${id}`, data);
    // Backend returns { message: "...", admin: {...} }
    const admin = response.data?.admin || response.admin;
    return {
      success: true,
      data: admin,
      message: response.data?.message || response.message || 'Organizational admin updated successfully'
    };
  } catch (error: any) {
    console.error('updateOrgAdmin error:', error);
    return {
      success: false,
      error: error.message || 'Failed to update organizational admin'
    };
  }
};

/**
 * Remove an organizational admin
 */
export const removeOrgAdmin = async (id: number): Promise<ApiResponse> => {
  try {
    const response = await httpClient.delete(`/org-admin/${id}`);
    // Backend returns { message: "Organizational admin removed successfully" }
    return {
      success: true,
      message: response.data?.message || response.message || 'Organizational admin removed successfully'
    };
  } catch (error: any) {
    console.error('removeOrgAdmin error:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove organizational admin'
    };
  }
};

/**
 * Get organization admin details with stats (domains, assessments, users)
 */
export const getOrgAdminDetails = async (id: string | number): Promise<ApiResponse<OrgAdminDetails>> => {
  try {
    const response = await httpClient.get(`/org-admin/${id}/details`);
    // Backend returns the OrgAdminDetails object directly
    return {
      success: true,
      data: response.data || response,
      message: 'Organization details fetched successfully'
    };
  } catch (error: any) {
    console.error('getOrgAdminDetails error:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch organization details'
    };
  }
};
